import { signInWithCustomToken } from "firebase/auth";
import { getFirebaseClient } from "./firebase";

const HASH_KEY = "ccsHandoff=";

/**
 * Consume a cross-panel auth handoff token from the URL fragment, if
 * present. Must be called AFTER `bootstrapFirebaseFromEnv` /
 * `initializeFirebaseClient` and BEFORE the app renders `<AuthProvider>`,
 * so `onAuthStateChanged` fires with the signed-in user rather than briefly
 * rendering the login screen.
 *
 * The token is carried in `location.hash` (never sent to any server) by the
 * panel that minted it via the `mintPanelHandoffToken` callable — see
 * `switchMembershipAndNavigate` in auth.tsx. The hash is stripped
 * immediately after reading so a refresh or shared link can't replay it.
 */
export async function consumeCrossPanelHandoff(): Promise<void> {
  if (typeof window === "undefined") return;
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash.startsWith(HASH_KEY)) return;

  const token = decodeURIComponent(hash.slice(HASH_KEY.length));

  // Strip the token from the URL immediately, before attempting to use it,
  // so it never lingers in browser history / can't be replayed.
  const cleanUrl = new URL(window.location.href);
  cleanUrl.hash = "";
  window.history.replaceState(null, "", cleanUrl.toString());

  if (!token) return;

  try {
    const { auth } = getFirebaseClient();
    await signInWithCustomToken(auth, token);
  } catch (err) {
    console.error("[crossPanelHandoff] sign-in with handoff token failed", err);
  }
}
